import { CloudSecurityContext, RouteNameToApplicationContextMap } from "@/constants/general.ts";
import type { RouteName } from "@/constants/routes.ts";
import { Service } from "@/constants/cloud-apps";
import { type CloudAppService, useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";

/**
 * Resolves the cloud security context for a given route, falling back to `CLOUD_SECURITY_POLICIES` when unmapped.
 * @param routeName
 */
export const getCloudSecurityContext = (routeName: RouteName): CloudSecurityContext => {
  return (
    RouteNameToApplicationContextMap[routeName] ?? CloudSecurityContext.CLOUD_SECURITY_POLICIES
  );
};

/**
 * Returns `true` when the given cloud service is present in the connected services list.
 * Returns `undefined` when no connected services have been loaded yet.
 * @sideEffect reads `useCloudAppsStore`
 */
export const isCloudAppConnected = (serviceName: Service) => {
  if (!useCloudAppsStore().connectedServices?.length) return;

  return useCloudAppsStore().connectedServices.some((service) => service.name === serviceName);
};

/**
 * Returns `true` when the given service is connected and has security awareness enabled.
 * Returns `undefined` when no connected services have been loaded yet.
 * @sideEffect reads `useCloudAppsStore`
 */
export const isSecurityAwarenessAllowedFor = (serviceName: Service) => {
  if (!useCloudAppsStore().connectedServices?.length) return;

  return useCloudAppsStore().connectedServices.some(
    (service) => service.name === serviceName && service.securityAwarenessAllowed
  );
};

/**
 * Returns `true` when the service has at least one permission that is required in the
 * current cloud security context but has not been granted.
 * @param service - The cloud app service to inspect.
 * @param routeName - The active route, used to determine the cloud security context.
 */
export function serviceHasMissingRequiredPermissions(
  service: CloudAppService,
  routeName: RouteName
) {
  return service.permissions.some((permission) => {
    if (permission.requiredInContext?.includes(getCloudSecurityContext(routeName))) {
      return !permission.granted;
    }
  });
}
